import React, { useState } from 'react';
import '../css/user/feed.css';
import LazyImage from '../../../components/LazyImage/LazyImage';
import { formatDuration, isUserLoggedIn } from '../../../shared/utils/function';
import { DEFAULT_USER_AVATAR, MEDIA_TYPE, PAYMENT_INFO } from '../../../shared/constants/common';
import PurchaseContentModal from '../../../views/Modals/PurchaseContent';
import { showInfo } from '../../../shared/utils/swalHandler';
import useNavigation from '../../../shared/utils/useNavigation';
import { ImagesIcon } from '../../../assets/Icons/SVG';
import { getAmountWithExtraCharge } from '../../../shared/utils/paymentUtil';

const LockedPPVContentPreview = ({
  purchase_type = 'content',
  isBundle = false,
  mediaArray = [],
  post = null,
  creatorInfo = null,
  onUnlockClick = null,
  isOpenFollowModal = null,
  vatPercentage = 0,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigation();
  const price = post?.price ? post.price : 0;
  const displayPrice = price ? getAmountWithExtraCharge(price, vatPercentage) : '0.00';
  const isFollowLoading = creatorInfo?.subscribed || false;

  const getMediaCounts = () => {
    if (!isBundle || !mediaArray || mediaArray.length === 0) {
      return { imageCount: 0, videoCount: 0 };
    }

    let imageCount = 0;
    let videoCount = 0;

    mediaArray.forEach(media => {
      if (media.type_file === 'video' || media.type === 'video') {
        videoCount++;
      } else {
        imageCount++;
      }
    });

    return { imageCount, videoCount };
  };

  // Determine if this post is video (for icon/label). Resilient when post.type_file is missing after navigation.
  const isVideoPost = () => {
    if (post?.type_file === MEDIA_TYPE.VIDEO) return true;
    if (mediaArray?.length === 1) {
      const first = mediaArray[0];
      if (first?.type_file === 'video' || first?.type === 'video') return true;
    }
    if (post?.duration != null && post?.duration > 0) return true;
    return false;
  };

  // Helper function to calculate total video duration
  const getTotalVideoDuration = () => {
    // For single video posts (check type_file or single media / duration)
    const isVideo = post?.type_file === MEDIA_TYPE.VIDEO ||
      (mediaArray?.length === 1 && (mediaArray[0]?.type_file === 'video' || mediaArray[0]?.type === 'video'));
    if (isVideo && post?.duration) {
      return typeof post.duration === 'number'
        ? post.duration
        : parseFloat(post.duration) || 0;
    }

    // For bundle content
    if (!isBundle || !mediaArray || mediaArray.length === 0) {
      return 0;
    }

    let totalDuration = 0;

    mediaArray.forEach(media => {
      if ((media.type_file === 'video' || media.type === 'video') && media.duration) {
        // Duration might be in seconds (number) or already a string
        const duration = typeof media.duration === 'number'
          ? media.duration
          : parseFloat(media.duration) || 0;
        totalDuration += duration;
      }
    });

    return totalDuration;
  };

  const onPurchaseContent = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!isUserLoggedIn()) {
      showInfo('', 'Log in or Sign up to purchase content', {
        showCancelButton: true,
        confirmButtonText: 'Login',
      }).then((result) => {
        if (result.isConfirmed) {
          navigate.toLogin();
        }
      });
      return;
    }

    // if user is not following, open follow modal
    // if (!isFollowLoading) {
    //   if (isOpenFollowModal) {
    //     isOpenFollowModal(true);
    //   }
    //   return;
    // }

    // Always close first to ensure clean state, then open
    // This ensures the useEffect in the modal runs again when reopening
    if (isModalOpen) {
      setIsModalOpen(false);
      // Use setTimeout to ensure state update completes before opening again
      setTimeout(() => {
        setIsModalOpen(true);
      }, 100);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleToggleModal = () => {
    setIsModalOpen(false);
  };

  const handlePurchaseSuccess = () => {
    if (onUnlockClick) {
      onUnlockClick(post);
    }

    return post;
  };

  return (
    <>
      <div className="post-media-container" style={{ position: 'relative' }}>
        <div className="lock-overlay">
          <div className="lock-content-box within_card">
            <div className="lock-user-profile">
              <div className="lock-avatar">
                <LazyImage
                  src={creatorInfo?.profile_picture || DEFAULT_USER_AVATAR}
                  alt={creatorInfo?.username}
                  className='cm-post-height'
                  onError={(e) => {
                    e.target.src = DEFAULT_USER_AVATAR;
                  }}
                />
              </div>

              <div className="unlock-text-title mt-3">
                UNLOCK TO VIEW
              </div>

              {isBundle && mediaArray && mediaArray.length > 0 ? (
                mediaArray.length === 1 ? (
                  /* Single item in bundle: show icon + "1 video" or "1 image" (no "Bundle" label) */
                  <div className="content-info">
                    {isVideoPost() ?
                      <div className="content-info" style={{ marginTop: '4px' }}>
                        <i className='fa-solid fa-video'></i>
                        1 video
                        <span className="separator">•</span>
                        {formatDuration(getTotalVideoDuration())}
                      </div>
                      :
                      <div className="content-info" style={{ marginTop: '4px' }}>
                        <i className='fa-solid fa-image'></i>
                        1 image
                      </div>
                    }
                  </div>
                ) : (
                <>
                  <div className="content-info">
                    <span>
                      <ImagesIcon /> Bundle
                    </span>
                    <span className="separator">•</span>
                    {getMediaCounts().imageCount > 0
                      ? (getMediaCounts().imageCount === 1 ? '1 image' : `${getMediaCounts().imageCount} images`)
                      : ''}
                    {getMediaCounts().imageCount > 0 && getMediaCounts().videoCount > 0 ? ', ' : ''}
                    {getMediaCounts().videoCount > 0
                      ? (getMediaCounts().videoCount === 1 ? '1 video' : `${getMediaCounts().videoCount} videos`)
                      : ''}
                  </div>

                  {/* Show total video duration */}
                  {getTotalVideoDuration() > 0 && (
                    <div className='d-flex justify-content-center'>
                      <span className='d-flex align-items-center'>
                        <i className="fa-solid fa-video"></i> &nbsp;
                        <span className="separator">•</span> &nbsp;
                        <span>{formatDuration(getTotalVideoDuration())}</span>
                      </span>
                    </div>
                  )}
                </>
                )
              ) : (
                <div className="content-info">
                  {isVideoPost() ?
                    <div className="content-info" style={{ marginTop: '4px' }}>
                      <i className='fa-solid fa-video'></i>
                      1 video
                      <span className="separator">•</span>
                      {formatDuration(getTotalVideoDuration())}
                    </div>
                    :
                    <div className="content-info" style={{ marginTop: '4px' }}>
                      <i className="fa-solid fa-image"></i>
                      1 image
                    </div>
                  }
                </div>
              )}

              <button
                type="button"
                className="purchase-button mt-3"
                onClick={onPurchaseContent}
              >
                Purchase for ${displayPrice}
              </button>
            </div>
          </div>


        </div>
      </div>

      <PurchaseContentModal
        isOpen={isModalOpen}
        toggle={handleToggleModal}
        contentId={post?._id || post?.id || null}
        contentPrice={price}
        creatorInfo={creatorInfo}
        post={post}
        onPurchaseSuccess={handlePurchaseSuccess}
        purchase_type={purchase_type}
      />
    </>
  );
};

export default LockedPPVContentPreview;
